import { useState, useEffect, useCallback, useRef } from 'react'
import { whatsappApi } from '../../services/api'

export interface WAContact {
  id: number
  phone: string
  display_name: string | null
  unread_count: number
  last_message_at: string | null
  tecnico_id: number | null
  is_group: boolean
}

export interface WAMessage {
  id: number
  direction: 'inbound' | 'outbound'
  status: string
  body: string
  created_at: string | null
  is_automated: boolean
  entity_type: string | null
  entity_id: number | null
}

export function useWhatsApp() {
  const [contacts, setContacts] = useState<WAContact[]>([])
  const [messages, setMessages] = useState<Record<number, WAMessage[]>>({})
  const [activeContact, setActiveContact] = useState<number | null>(null)
  const [connectionState, setConnectionState] = useState<string>('connecting')
  const wsRef = useRef<WebSocket | null>(null)
  const activeContactRef = useRef<number | null>(null)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // tieni il ref sincronizzato senza causare re-render
  useEffect(() => { activeContactRef.current = activeContact }, [activeContact])

  const fetchContacts = useCallback(async () => {
    try {
      const res = await whatsappApi.contacts()
      setContacts(res.data)
    } catch { /* silent */ }
  }, [])

  const fetchMessages = useCallback(async (contactId: number) => {
    try {
      const res = await whatsappApi.messages(contactId)
      setMessages(prev => ({ ...prev, [contactId]: res.data }))
      await whatsappApi.markRead(contactId)
      setContacts(prev => prev.map(c => c.id === contactId ? { ...c, unread_count: 0 } : c))
    } catch { /* silent */ }
  }, [])

  const sendMessage = useCallback(async (contactId: number, text: string) => {
    await whatsappApi.sendMessage(contactId, text)
    await fetchMessages(contactId)
    setContacts(prev => {
      const c = prev.find(x => x.id === contactId)
      if (!c) return prev
      return [{ ...c }, ...prev.filter(x => x.id !== contactId)]
    })
  }, [fetchMessages])

  const sendGroup = useCallback(async (message: string) => {
    await whatsappApi.sendGroup(message)
  }, [])

  const checkStatus = useCallback(async () => {
    try {
      const res = await whatsappApi.status()
      setConnectionState(res.data.state)
    } catch {
      setConnectionState('unreachable')
    }
  }, [])

  // WebSocket stabile — NON dipende da activeContact (usa il ref)
  const connectWS = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const token = localStorage.getItem('xsophia_token') || ''
    const ws = new WebSocket(`${proto}://${window.location.host}/api/ws/0?token=${token}`)
    wsRef.current = ws

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data)
        if (data.type === 'whatsapp_new_message') {
          fetchContacts()
          const current = activeContactRef.current
          if (current !== null && data.contact_id === current) {
            fetchMessages(current)
          }
        } else if (data.type === 'whatsapp_connection') {
          setConnectionState(data.state)
        }
      } catch { /* malformed */ }
    }

    ws.onclose = () => {
      // riconnette dopo 3s
      reconnectTimer.current = setTimeout(connectWS, 3000)
    }

    ws.onerror = () => { ws.close() }
  }, [fetchContacts, fetchMessages])

  useEffect(() => {
    connectWS()
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      wsRef.current?.close()
    }
  }, [connectWS])

  // init: sync contatti + status polling
  useEffect(() => {
    whatsappApi.syncContacts().catch(() => {})
    fetchContacts()
    checkStatus()
    const statusTimer = setInterval(checkStatus, 60_000)
    return () => clearInterval(statusTimer)
  }, [fetchContacts, checkStatus])

  // carica messaggi quando si seleziona un contatto
  useEffect(() => {
    if (activeContact !== null) fetchMessages(activeContact)
  }, [activeContact, fetchMessages])

  // polling fallback ogni 5s sul contatto attivo (copre eventuali buchi WS)
  useEffect(() => {
    if (activeContact === null) return
    const t = setInterval(() => fetchMessages(activeContact), 5000)
    return () => clearInterval(t)
  }, [activeContact, fetchMessages])

  const totalUnread = contacts.reduce((sum, c) => sum + c.unread_count, 0)

  return {
    contacts, messages, activeContact, setActiveContact,
    connectionState, totalUnread,
    sendMessage, sendGroup, fetchContacts,
  }
}
